/**
 * Work-order section 6: "Komponenter" + "Hitta komponenterna".
 *
 * Pure presentational: props in, callbacks out. No Firebase, no app contexts —
 * the guest page renders it outside every provider. Image URLs arrive already
 * resolved; a null `imageUrl` renders a "Bild saknas" placeholder.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { MapPin, ExternalLink, Image as ImageIcon, Settings, XCircle } from 'lucide-react';
import { SectionTitle } from './SectionTitle';
import ConfirmationModal from '../../../../Components/ConfirmationModal';
import { WORK_ORDER_LABELS, workOrderSectionTestId } from '../../workOrderSections';
import './componentList.css';

export interface ComponentLocationFrame {
    width: number;
    height: number;
}

export interface ComponentLocationImage {
    /** e.g. "Plats på foto", a level name, "Takplan", "Karta". */
    label: string;
    /** Already-resolved URL; null renders a "Bild saknas" placeholder. */
    imageUrl: string | null;
    /** Still resolving the URL: renders a spinner instead of the placeholder. */
    loading?: boolean;
    /** Wired to the <img onError>, e.g. to swap a missing size variant for the original. */
    onImgError?: () => void;
    /** Marker position as a fraction of the image (0..1). */
    x: number;
    y: number;
    /**
     * width / height of the reference frame. Only needed by `renderOverlay`,
     * which gets the frame's rendered size derived from it.
     */
    aspectRatio?: number;
    /**
     * Optional layer drawn over the raster at the frame's rendered size (the
     * guest page draws a drawing's vector PDF here). Rendered only once the
     * frame has a measured width.
     */
    renderOverlay?: (frame: ComponentLocationFrame) => React.ReactNode;
}

export interface ComponentListItem {
    key: string;
    name: string;
    subtitle?: string;
    locations?: ComponentLocationImage[];
    /** Renders "GPS: lat, lng" + "Öppna i kartor". */
    gps?: { lat: number; lng: number };
    /** When given, the name links out (target _blank) — admin → component page. */
    href?: string;
}

export interface ComponentListProps {
    components: ComponentListItem[];
    emptyText?: string;
    /** Renders a "Lägg till" button. */
    onAdd?: () => void;
    /** Renders "Ta bort komponent" per item, behind a ConfirmationModal. */
    onRemove?: (key: string) => void;
    /** Alternative to `href` (guest scrolls); renders "Visa". */
    onView?: (key: string) => void;
}

export const MarkerImage: React.FC<{ image: ComponentLocationImage }> = ({ image }) => {
    const frameRef = useRef<HTMLDivElement>(null);
    const [frameWidth, setFrameWidth] = useState(0);
    const hasOverlay = Boolean(image.renderOverlay);
    const frameHeight = image.aspectRatio && image.aspectRatio > 0 ? frameWidth / image.aspectRatio : 0;

    // The overlay is sized from the frame, which follows the image's width.
    useEffect(() => {
        if (!hasOverlay || typeof ResizeObserver === 'undefined') return;
        const el = frameRef.current;
        if (!el) return;
        const update = () => setFrameWidth(el.offsetWidth);
        const observer = new ResizeObserver(update);
        observer.observe(el);
        update();
        return () => observer.disconnect();
    }, [hasOverlay, image.imageUrl]);

    const hasPosition = Number.isFinite(image.x) && Number.isFinite(image.y);

    return (
        <div className="wo-component-locations__image">
            <div ref={frameRef} className="wo-component-locations__frame">
                {image.imageUrl ? (
                    <>
                        <img src={image.imageUrl} alt={image.label} onError={image.onImgError} />
                        {image.renderOverlay && frameWidth > 0 && frameHeight > 0 &&
                            image.renderOverlay({ width: frameWidth, height: frameHeight })}
                        {hasPosition && (
                            <span
                                aria-hidden
                                data-testid="wo-component-marker"
                                className="wo-component-locations__marker"
                                style={{ left: `${image.x * 100}%`, top: `${image.y * 100}%` }}
                            />
                        )}
                    </>
                ) : image.loading ? (
                    <div className="wo-component-locations__placeholder bg-light">
                        <Spinner animation="border" size="sm" />
                    </div>
                ) : (
                    <div className="wo-component-locations__placeholder bg-light text-muted">
                        <ImageIcon size={24} />
                        <small className="mt-1">Bild saknas</small>
                    </div>
                )}
            </div>
            <small className="text-muted d-block mt-1">{image.label}</small>
        </div>
    );
};

export interface ComponentLocationsProps {
    components: ComponentListItem[];
}

/**
 * The "Hitta komponenterna" part on its own: every component's location images
 * with its marker, plus GPS. Renders nothing when no component has any
 * location data. Used by `ComponentList` and by the guest wrapper.
 */
export const ComponentLocations: React.FC<ComponentLocationsProps> = ({ components }) => {
    const located = components.filter((c) => (c.locations && c.locations.length > 0) || c.gps);
    if (located.length === 0) return null;

    return (
        <div className="wo-component-locations">
            <h3 className="wo-section-title">
                <MapPin size={16} className="wo-section-title__icon" aria-hidden="true" />
                <span>{WORK_ORDER_LABELS.sectionFindComponents}</span>
            </h3>
            {located.map((component, idx) => (
                <div key={component.key} className={`wo-component-locations__component${idx > 0 ? ' wo-component-locations__component--divided' : ''}`}>
                    <div className="wo-component-locations__name">{component.name}</div>

                    {component.locations && component.locations.length > 0 && (
                        <div className="wo-component-locations__images">
                            {component.locations.map((image, i) => (
                                <MarkerImage key={`${image.label}-${i}`} image={image} />
                            ))}
                        </div>
                    )}

                    {component.gps && (
                        <div className="wo-component-locations__gps">
                            <MapPin size={14} className="wo-section-title__icon" aria-hidden="true" />
                            <span>GPS: {component.gps.lat.toFixed(5)}, {component.gps.lng.toFixed(5)}</span>
                            <a
                                href={`https://www.google.com/maps?q=${component.gps.lat},${component.gps.lng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="wo-component-locations__maps-link"
                            >
                                <ExternalLink size={14} aria-hidden="true" /> {WORK_ORDER_LABELS.openInMaps}
                            </a>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

const ComponentList: React.FC<ComponentListProps> = ({
    components,
    emptyText = 'Inga komponenter',
    onAdd,
    onRemove,
    onView,
}) => {
    const [removeKey, setRemoveKey] = useState<string | null>(null);
    const removeTarget = removeKey ? components.find((c) => c.key === removeKey) : undefined;

    const handleRemoveConfirm = () => {
        if (removeKey && onRemove) onRemove(removeKey);
        setRemoveKey(null);
    };

    return (
        <section className="wo-component-list wo-card" data-testid={workOrderSectionTestId('components')}>
            <SectionTitle
                icon={Settings}
                title={WORK_ORDER_LABELS.sectionComponents}
                onAdd={onAdd}
                addLabel={WORK_ORDER_LABELS.addComponent}
            />

            {components.length === 0 ? (
                <p className="text-muted mb-0">{emptyText}</p>
            ) : (
                <ul className="wo-component-list__items">
                    {components.map((component) => (
                        <li key={component.key} id={`wo-component-${component.key}`} className="wo-component-list__item">
                            <div className="wo-component-list__item-name">
                                {component.href ? (
                                    <a href={component.href} target="_blank" rel="noopener noreferrer" className="wo-component-list__link">
                                        {component.name} <ExternalLink size={12} aria-hidden="true" />
                                    </a>
                                ) : (
                                    <span className="wo-component-list__link">{component.name}</span>
                                )}
                                {component.subtitle && (
                                    <div className="wo-component-list__subtitle">{component.subtitle}</div>
                                )}
                            </div>
                            {(onView || onRemove) && (
                                <div className="wo-component-list__item-actions">
                                    {onView && !component.href && (
                                        <Button variant="link" className="wo-edit-link" onClick={() => onView(component.key)}>
                                            {WORK_ORDER_LABELS.show}
                                        </Button>
                                    )}
                                    {onRemove && (
                                        <Button
                                            variant="link"
                                            className="wo-component-list__remove"
                                            onClick={() => setRemoveKey(component.key)}
                                            aria-label={`${WORK_ORDER_LABELS.removeComponent}: ${component.name}`}
                                            title={WORK_ORDER_LABELS.removeComponent}
                                        >
                                            <XCircle size={20} aria-hidden="true" />
                                        </Button>
                                    )}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            <ComponentLocations components={components} />

            {onRemove && (
                <ConfirmationModal
                    show={removeKey !== null}
                    title={WORK_ORDER_LABELS.removeComponent}
                    message={`Vill du ta bort ${removeTarget?.name ?? 'komponenten'} från ärendet? Komponenten själv påverkas inte.`}
                    confirmText={WORK_ORDER_LABELS.removeComponent}
                    variant="danger"
                    onConfirm={handleRemoveConfirm}
                    onCancel={() => setRemoveKey(null)}
                />
            )}
        </section>
    );
};

export default ComponentList;
